import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createRental, closeProperty } from '../../../Redux/Actions/actions';
import { FaHome, FaUser, FaDollarSign, FaCalendarAlt, FaTimes, FaExternalLinkAlt } from 'react-icons/fa';
import style from './NewRentalContract.module.css';

const NewRentalContract = ({ onClose }) => {
  const dispatch = useDispatch();
  const { rentalsLoading } = useSelector(state => state);
  const [currentStep, setCurrentStep] = useState(1);
  const [availableProperties, setAvailableProperties] = useState([]);
  const [loadingProperties, setLoadingProperties] = useState(false);
  
  const [formData, setFormData] = useState({
    // Información de la propiedad
    propertyId: '',
    
    // Términos del contrato
    rentValue: '',
    currency: 'ARS',
    contractStart: '',
    contractEnd: '',
    paymentDueDay: 10,
    
    // Configuración de ajustes
    adjustmentType: 'ICL',
    adjustmentFrequency: 12,
    
    // Depósito y gastos
    depositAmount: '',
    expensesAmount: '',
    
    // Información del propietario
    ownerName: '',
    ownerBankAccount: '',
    
    // Configuración de mora
    lateInterestRate: 0.05,
    gracePeriodDays: 3,
    
    // Inquilino principal
    tenantName: '',
    tenantDNI: '',
    tenantEmail: '',
    tenantPhone: '',
    tenantOccupation: '',
    tenantMonthlyIncome: ''
  });

  useEffect(() => {
    // Cargar propiedades disponibles para alquiler
    loadAvailableProperties();
  }, []);

  const loadAvailableProperties = async () => {
    console.log('Iniciando carga de propiedades...');
    setLoadingProperties(true);
    try {
      // Usar URL completa del servidor ByRaices
      const apiUrl = 'https://server.byraices.com/properties/active/rent';
      console.log('Llamando a:', apiUrl);
      
      const response = await fetch(apiUrl);
      console.log('Respuesta del servidor:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('Datos recibidos:', data);
        
        // La respuesta viene con { properties: [...] }
        const properties = data.properties || [];
        console.log('Propiedades procesadas:', properties.length);
        setAvailableProperties(properties);
      } else {
        console.error('Error loading properties:', response.status);
        setAvailableProperties([]);
      }
    } catch (error) {
      console.error('Error loading properties:', error);
      setAvailableProperties([]);
    } finally {
      setLoadingProperties(false);
      console.log('Carga de propiedades finalizada');
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) || '' : value
    }));
  };

  const calculateContractEnd = (startDate, months = 36) => {
    const start = new Date(startDate);
    const end = new Date(start);
    end.setMonth(end.getMonth() + months);
    return end.toISOString().split('T')[0];
  };

  const handleContractStartChange = (e) => {
    const startDate = e.target.value;
    setFormData(prev => ({
      ...prev,
      contractStart: startDate,
      contractEnd: calculateContractEnd(startDate)
    }));
  };

  const handleNext = () => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      console.log('=== INICIANDO PROCESO DE CREACIÓN DE CONTRATO ===');
      console.log('FormData completo:', formData);
      
      // Validaciones
      if (!formData.propertyId) {
        alert('Por favor seleccione una propiedad');
        return;
      }
      
      if (!formData.rentValue || parseFloat(formData.rentValue) <= 0) {
        alert('Por favor ingrese un valor de alquiler válido');
        return;
      }
      
      // Crear el contrato (esto automáticamente cerrará la propiedad primero)
      const contractData = {
        propertyId: parseInt(formData.propertyId),
        rentValue: parseFloat(formData.rentValue),
        currency: formData.currency,
        contractStart: formData.contractStart,
        contractEnd: formData.contractEnd,
        paymentDueDay: parseInt(formData.paymentDueDay),
        adjustmentType: formData.adjustmentType,
        adjustmentFrequency: parseInt(formData.adjustmentFrequency),
        depositAmount: parseFloat(formData.depositAmount) || 0,
        expensesAmount: parseFloat(formData.expensesAmount) || 0,
        ownerName: formData.ownerName || '',
        ownerBankAccount: formData.ownerBankAccount || '',
        lateInterestRate: parseFloat(formData.lateInterestRate) || 0.05,
        gracePeriodDays: parseInt(formData.gracePeriodDays) || 3,
        
        // Datos del inquilino
        tenantName: formData.tenantName || '',
        tenantDNI: formData.tenantDNI || '',
        tenantEmail: formData.tenantEmail || '',
        tenantPhone: formData.tenantPhone || '',
        tenantOccupation: formData.tenantOccupation || '',
        tenantMonthlyIncome: parseFloat(formData.tenantMonthlyIncome) || 0
      };
      
      console.log('Datos del contrato a crear:', contractData);

      await dispatch(createRental(contractData));
      alert('Contrato creado exitosamente y propiedad cerrada');
      
      // Reset form y cerrar modal
      setFormData({
        propertyId: '',
        rentValue: '',
        currency: 'ARS',
        contractStart: '',
        contractEnd: '',
        paymentDueDay: 10,
        adjustmentType: 'ICL',
        adjustmentFrequency: 12,
        depositAmount: '',
        expensesAmount: '',
        ownerName: '',
        ownerBankAccount: '',
        lateInterestRate: 0.05,
        gracePeriodDays: 3,
        tenantName: '',
        tenantDNI: '',
        tenantEmail: '',
        tenantPhone: '',
        tenantOccupation: '',
        tenantMonthlyIncome: ''
      });
      setCurrentStep(1);
      
      // Cerrar modal si existe la función
      if (onClose) {
        onClose();
      }
      
    } catch (error) {
      console.error('=== ERROR AL CREAR CONTRATO ===');
      console.error('Error completo:', error);
      console.error('Respuesta del servidor:', error.response?.data);
      
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          'Error al crear el contrato';
      
      alert(`Error: ${errorMessage}`);
    }
  };

  const renderStep1 = () => (
    <div className={style.step}>
      <h3 className={style.stepTitle}>
        <FaHome /> Información del Contrato
      </h3>
      
      <div className={style.formGrid}>
        <div className={style.formGroup}>
          <label>Seleccionar Propiedad*</label>
          {loadingProperties ? (
            <div className={style.loadingState}>
              <div className={style.spinner}></div>
              <span>Cargando propiedades...</span>
            </div>
          ) : (
            <>
              <select
                name="propertyId"
                value={formData.propertyId}
                onChange={handleInputChange}
                required
                className={style.input}
              >
                <option value="">-- Seleccione una propiedad --</option>
                {availableProperties.length > 0 ? (
                  availableProperties.map(property => {
                    const displayTitle = `ID: ${property.id} - ${property.title || 
                      `${property.street} ${property.number}, ${property.locality}, ${property.province}`}`;
                    return (
                      <option key={property.id} value={property.id}>
                        {displayTitle}
                      </option>
                    );
                  })
                ) : (
                  <option value="" disabled>No hay propiedades disponibles</option>
                )}
              </select>
              
              {/* Debug info - remover en producción */}
              <div style={{fontSize: '0.8rem', color: '#666', marginTop: '4px'}}>
                Debug: {availableProperties.length} propiedades cargadas
              </div>
              
              {formData.propertyId && (
                <div className={style.propertyDetails}>
                  {(() => {
                    const selectedProperty = availableProperties.find(p => p.id == formData.propertyId);
                    return selectedProperty ? (
                      <div className={style.propertyInfo}>
                        <div className={style.propertyInfoItem}>
                          <span className={style.label}>Dirección:</span>
                          <span>{selectedProperty.street} {selectedProperty.number}, {selectedProperty.locality}</span>
                        </div>
                        <div className={style.propertyInfoItem}>
                          <span className={style.label}>Tipo:</span>
                          <span>{selectedProperty.propertyType}</span>
                        </div>
                        <div className={style.propertyInfoItem}>
                          <span className={style.label}>Precio:</span>
                          <span>{selectedProperty.currency === 'ARG' ? 'ARS' : selectedProperty.currency} ${parseInt(selectedProperty.price).toLocaleString()}</span>
                        </div>
                        <a
                          href={`https://byraices.com/detail/${selectedProperty.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={style.propertyLink}
                        >
                          <FaExternalLinkAlt />
                          Ver propiedad en ByRaíces
                        </a>
                      </div>
                    ) : null;
                  })()} 
                </div>
              )}
            </>
          )}
        </div>

        <div className={style.formGroup}>
          <label>Valor del Alquiler*</label>
          <div className={style.inputWithCurrency}>
            <select 
              name="currency" 
              value={formData.currency}
              onChange={handleInputChange}
              className={style.currencySelect}
            >
              <option value="ARS">ARS</option>
              <option value="USD">USD</option>
            </select>
            <input
              type="number"
              name="rentValue"
              value={formData.rentValue}
              onChange={handleInputChange}
              placeholder="150000"
              required
              className={style.input}
            />
          </div>
        </div>

        <div className={style.formGroup}>
          <label>Fecha de Inicio*</label>
          <input
            type="date"
            name="contractStart"
            value={formData.contractStart}
            onChange={handleContractStartChange}
            required
            className={style.input}
          />
        </div>

        <div className={style.formGroup}>
          <label>Fecha de Fin*</label>
          <input
            type="date"
            name="contractEnd"
            value={formData.contractEnd}
            onChange={handleInputChange}
            required
            className={style.input}
          />
        </div>

        <div className={style.formGroup}>
          <label>Día de Vencimiento (1-31)*</label>
          <input
            type="number"
            name="paymentDueDay"
            value={formData.paymentDueDay}
            onChange={handleInputChange}
            min="1"
            max="31"
            required
            className={style.input}
          />
        </div>

        <div className={style.formGroup}>
          <label>Tipo de Ajuste*</label>
          <select 
            name="adjustmentType" 
            value={formData.adjustmentType}
            onChange={handleInputChange}
            className={style.input}
          >
            <option value="ICL">ICL (Índice de Contratos de Locación)</option>
            <option value="IPC">IPC (Índice de Precios al Consumidor)</option>
            <option value="porcentual">Porcentual</option>
            <option value="manual">Manual</option>
            <option value="sin-ajuste">Sin Ajuste</option>
          </select>
        </div>

        <div className={style.formGroup}>
          <label>Frecuencia de Ajuste (meses)*</label>
          <input
            type="number"
            name="adjustmentFrequency"
            value={formData.adjustmentFrequency}
            onChange={handleInputChange}
            min="1"
            max="36"
            required
            className={style.input}
          />
        </div>

        <div className={style.formGroup}>
          <label>Monto del Depósito</label>
          <input
            type="number"
            name="depositAmount"
            value={formData.depositAmount}
            onChange={handleInputChange}
            placeholder="150000"
            className={style.input}
          />
        </div>

        <div className={style.formGroup}>
          <label>Expensas Mensuales</label>
          <input
            type="number"
            name="expensesAmount"
            value={formData.expensesAmount}
            onChange={handleInputChange}
            placeholder="25000"
            className={style.input}
          />
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className={style.step}>
      <h3 className={style.stepTitle}>
        <FaDollarSign /> Información del Propietario
      </h3>
      
      <div className={style.formGrid}>
        <div className={style.formGroup}>
          <label>Nombre del Propietario*</label>
          <input
            type="text"
            name="ownerName"
            value={formData.ownerName}
            onChange={handleInputChange}
            placeholder="Pedro González"
            required
            className={style.input}
          />
        </div>

        <div className={style.formGroup}>
          <label>CBU/Alias Bancario</label>
          <input
            type="text"
            name="ownerBankAccount"
            value={formData.ownerBankAccount}
            onChange={handleInputChange}
            placeholder="0123456789012345678901"
            className={style.input}
          />
        </div>

        <div className={style.formGroup}>
          <label>Tasa de Interés por Mora (% diario)</label>
          <input
            type="number"
            name="lateInterestRate"
            value={formData.lateInterestRate}
            onChange={handleInputChange}
            step="0.01"
            min="0"
            max="1"
            placeholder="0.05"
            className={style.input}
          />
          <small className={style.helpText}>
            Ejemplo: 0.05 = 0.05% por día de atraso
          </small>
        </div>

        <div className={style.formGroup}>
          <label>Días de Gracia</label>
          <input
            type="number"
            name="gracePeriodDays"
            value={formData.gracePeriodDays}
            onChange={handleInputChange}
            min="0"
            max="30"
            placeholder="3"
            className={style.input}
          />
          <small className={style.helpText}>
            Días antes de aplicar intereses por mora
          </small>
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className={style.step}>
      <h3 className={style.stepTitle}>
        <FaUser /> Inquilino Principal
      </h3>
      
      <div className={style.formGrid}>
        <div className={style.formGroup}>
          <label>Nombre Completo*</label>
          <input
            type="text"
            name="tenantName"
            value={formData.tenantName}
            onChange={handleInputChange}
            placeholder="Ana Martínez"
            required
            className={style.input}
          />
        </div>

        <div className={style.formGroup}>
          <label>DNI*</label>
          <input
            type="text"
            name="tenantDNI"
            value={formData.tenantDNI}
            onChange={handleInputChange}
            placeholder="35123456"
            required
            className={style.input}
          />
        </div>

        <div className={style.formGroup}>
          <label>Email*</label>
          <input
            type="email"
            name="tenantEmail"
            value={formData.tenantEmail}
            onChange={handleInputChange}
            placeholder="ana@email.com"
            required
            className={style.input}
          />
        </div>

        <div className={style.formGroup}>
          <label>Teléfono*</label>
          <input
            type="tel"
            name="tenantPhone"
            value={formData.tenantPhone}
            onChange={handleInputChange}
            placeholder="+5491112345678"
            required
            className={style.input}
          />
        </div>

        <div className={style.formGroup}>
          <label>Ocupación</label>
          <input
            type="text"
            name="tenantOccupation"
            value={formData.tenantOccupation}
            onChange={handleInputChange}
            placeholder="Empleado"
            className={style.input}
          />
        </div>

        <div className={style.formGroup}>
          <label>Ingresos Mensuales</label>
          <input
            type="number"
            name="tenantMonthlyIncome"
            value={formData.tenantMonthlyIncome}
            onChange={handleInputChange}
            placeholder="250000"
            className={style.input}
          />
        </div>
      </div>
    </div>
  );

  return (
    <div className={style.container}>
      <div className={style.header}>
        <div className={style.headerContent}>
          <h2 className={style.title}>Nuevo Contrato de Alquiler</h2>
          {onClose && (
            <button 
              onClick={onClose}
              className={style.closeButton}
              type="button"
              title="Cerrar"
            >
              <FaTimes />
            </button>
          )}
        </div>
        
        <div className={style.stepIndicator}>
          <div className={`${style.stepDot} ${currentStep >= 1 ? style.active : ''}`}>1</div>
          <div className={`${style.stepDot} ${currentStep >= 2 ? style.active : ''}`}>2</div>
          <div className={`${style.stepDot} ${currentStep >= 3 ? style.active : ''}`}>3</div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className={style.form}>
        {currentStep === 1 && renderStep1()}
        {currentStep === 2 && renderStep2()}
        {currentStep === 3 && renderStep3()}

        <div className={style.formActions}>
          {currentStep > 1 && (
            <button 
              type="button" 
              onClick={handlePrevious}
              className={`${style.button} ${style.secondary}`}
            >
              Anterior
            </button>
          )}
          
          {currentStep < 3 ? (
            <button 
              type="button" 
              onClick={handleNext}
              className={`${style.button} ${style.primary}`}
            >
              Siguiente
            </button>
          ) : (
            <button 
              type="submit" 
              disabled={rentalsLoading}
              className={`${style.button} ${style.primary}`}
            >
              {rentalsLoading ? 'Creando...' : 'Crear Contrato'}
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default NewRentalContract;